
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { AuthStateType } from "../types/auth-state";
import { authStateCoordinator, withAuthTransition } from "@/utils/authStateCoordinator";

// Realtime security guard: immediately signs user out when suspended or removed from company
export function useSecurityRealtimeGuard(authState: AuthStateType) {
  const { user } = authState;
  const notify = useNotify();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.id) return;
    
    // Skip security monitoring for users who haven't completed profile setup
    if (!authState.profile?.profile_completed) {
      console.log('[SecurityGuard] Skipping security monitoring - profile not completed');
      return;
    }
    
    console.log('[SecurityGuard] Setting up realtime listeners for user:', user.id);

    // Immediate server-side check to catch current state
    (async () => {
      try {
        const { data, error } = await supabase.rpc("get_user_security_status");
        if (error) throw error;
        const statusData: any = data as any;
        const status = statusData?.status;
        if (status === "suspended") {
          const suspensionSource = statusData?.suspension_source;
          if (suspensionSource !== 'company') {
            // Only navigate for individual suspensions
            // Company suspensions are handled by ProtectedRoute
            console.log('[SecurityGuard] User is individually suspended - navigating to /suspended');
            notify.error("Account suspended — Your account has been suspended. Please contact support for assistance.");
            await withAuthTransition('navigation', async () => {
              navigate("/suspended", { replace: true });
            }, user.id, 'Account suspended');
          }
        } else if (status === "deactivated") {
          notify.error("Account deactivated — Your access has been restricted due to deactivation.");
          // Navigate to deactivated screen instead of signing out
          await withAuthTransition('navigation', async () => {
            navigate("/deactivated", {
              replace: true,
              state: {
                deactivatedAt: statusData?.updated_at || new Date().toISOString(),
                deactivationReason: statusData?.deactivation_reason || "Account has been deactivated by an administrator",
              },
            });
          }, user.id, 'Account deactivated');
        } else if (status === "no_company" || status === "removed_from_company") {
          notify.error("Company access removed — You are not associated with a company. Join or create one to continue.");
          // Phase 2: Use coordinated navigation
          await withAuthTransition('navigation', async () => {
            navigate("/account/no-company", { replace: true });
          }, user.id, 'No company access');
        } else if (status === "no_profile" || status === "profile_incomplete") {
          await withAuthTransition('navigation', async () => {
            navigate("/profile-setup", { replace: true });
          }, user.id, 'Profile setup required');
        }
      } catch (err) {
        console.error("[SecurityGuard] Initial status check failed", err);
      }
    })();

    const channel = supabase
      .channel(`security-guard-${user.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${user.id}` },
        async (payload) => {
          console.log('[SecurityGuard] Profile UPDATE event received:', payload);
          const next = payload.new as any;
          if (next?.status === "suspended") {
            // Check if this is a company-level suspension
            // by checking suspended_by_company in the payload
            const memberData = payload.new as any;

            // Re-fetch security status to get suspension_source
            try {
              const { data: statusData } = await supabase
                .rpc("get_user_security_status");
              const suspensionSource = (statusData as any)?.suspension_source;

              if (suspensionSource === 'company') {
                // Company suspension — ProtectedRoute handles
                // the redirect via Branch 2, no navigation needed here
                if (authState.refreshProfile) {
                  await authState.refreshProfile();
                }
              } else {
                // Individual suspension — navigate to suspended screen
                notify.error("Account suspended — Your account has been suspended. Please contact support for assistance.");
                await withAuthTransition('navigation', async () => {
                  navigate("/suspended", { replace: true });
                }, user.id, 'Real-time suspension detected');
                if (authState.refreshProfile) {
                  await authState.refreshProfile();
                }
              }
            } catch (err) {
              console.error('[SecurityGuard] Failed to check suspension source', err);
            }
          } else if (
            next?.status === 'active' &&
            (payload.old as any)?.status === 'suspended'
          ) {
            console.log('[SecurityGuard] Profile unsuspended - refreshing profile then redirecting');

            // Wait for profile refresh to complete so
            // ProtectedRoute sees the updated status
            // before we navigate to /dashboard
            if (authState.refreshProfile) {
              await authState.refreshProfile();
            }

            // Add a small delay to ensure React state
            // has propagated from refreshProfile before
            // ProtectedRoute evaluates the new path
            await new Promise(resolve => setTimeout(resolve, 300));

            await withAuthTransition('navigation', async () => {
              navigate('/dashboard', { replace: true });
            }, user.id, 'User unsuspended - returning to dashboard');
          } else if (next?.company_id === null) {
            // Re-check status using RPC instead of direct company_id check
            try {
              const { data, error } = await supabase.rpc("get_user_security_status");
              if (error) throw error;
              const statusData: any = data as any;
              const status = statusData?.status;
              if (status === "no_company" || status === "removed_from_company") {
                notify.error("Company access removed — You are not associated with a company. Join or create one to continue.");
                // Phase 2: Use coordinated navigation
                await withAuthTransition('navigation', async () => {
                  navigate("/account/no-company", { replace: true });
                }, user.id, 'Company removed via realtime');
              }
            } catch (err) {
              console.error("[SecurityGuard] Status recheck failed", err);
            }
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "company_members", filter: `user_id=eq.${user.id}` },
        async (payload) => {
          console.log('[SecurityGuard] Company member DELETE event received:', payload);
          notify.error("Company access removed — You are not associated with a company. Join or create one to continue.");
          // Phase 2: Use coordinated navigation
          await withAuthTransition('navigation', async () => {
            navigate("/account/no-company", { replace: true });
          }, user.id, 'Company membership deleted via realtime');
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "company_members", filter: `user_id=eq.${user.id}` },
        async (payload) => {
          console.log('[SecurityGuard] Company member UPDATE event received:', payload);
          const prev = payload.old as any;
          const next = payload.new as any;
          
          // Handle reactivation: status changed from "deactivated" to "active"
          if (prev?.status === "deactivated" && next?.status === "active") {
            console.log('[SecurityGuard] REACTIVATION DETECTED: User status changed from deactivated to active');
            notify.success("Account Reactivated — You now have full access to the system.");
            
            // Force refresh profile to update local state
            if (authState.refreshProfile) {
              console.log('[SecurityGuard] Triggering profile refresh after reactivation');
              await authState.refreshProfile();
            }
            
            // Navigate back to dashboard if user is currently on deactivated page
            if (window.location.pathname === '/deactivated') {
              console.log('[SecurityGuard] User reactivated from deactivated page, redirecting to dashboard');
              await withAuthTransition('navigation', async () => {
                navigate("/dashboard", { replace: true });
              }, user.id, 'User reactivated - returning to dashboard');
            }
            return;
          }
          
          // Handle deactivation
          if (next?.status === "deactivated") {
            console.log('[SecurityGuard] DEACTIVATION DETECTED: User status changed to deactivated');
            
            // Force refresh profile to update local state IMMEDIATELY
            if (authState.refreshProfile) {
              console.log('[SecurityGuard] Triggering immediate profile refresh after deactivation');
              await authState.refreshProfile();
            }
            
            notify.error("Account deactivated — Your access has been restricted due to deactivation.");
            
            // Navigate to deactivated screen instead of signing out
            await withAuthTransition('navigation', async () => {
              navigate("/deactivated", { 
                replace: true,
                state: { 
                  deactivatedAt: next.updated_at || new Date().toISOString(),
                  deactivationReason: next.deactivation_reason || "Account has been deactivated by an administrator"
                }
              });
            }, user.id, 'User deactivated via realtime');
          }
        }
      )
      .subscribe((status) => {
        console.log('[SecurityGuard] Channel subscription status:', status);
      });

    return () => {
      console.log('[SecurityGuard] Cleaning up realtime channel for user:', user.id);
      supabase.removeChannel(channel);
    };
  }, [user?.id, authState.profile?.profile_completed]);
}
